/**
 * Tarjetas dentro del chat. Se dibujan con los datos vigentes (no con una copia del momento
 * de la respuesta): si marcas un hábito o mueves una tarea, la tarjeta ya lo refleja.
 */
import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { AlertTriangle, CalendarDays } from 'lucide-react';
import { addDays, type LocalDate } from '@habitos/engine';
import { useI18n } from '../i18n';
import { useApp } from '../state';
import { buildWeek } from '../domain/agenda';
import { setTaskDone } from '../domain/actions';
import { toEngineSettings, type HabitRec } from '../domain/model';
import { todayFor } from '../domain/selectors';
import { conflictNames, TimeRange } from './AgendaView';
import { HabitCard } from './HabitCard';

export function AgendaCard({ from, to }: { from: string; to: string }) {
  const { t, fmtDate } = useI18n();
  const { data, ctx, idx } = useApp();
  const settings = useMemo(() => toEngineSettings(ctx.settings), [ctx.settings]);
  const days = useMemo(() => {
    const week = buildWeek({ habits: data.habits, reminders: data.reminders, notes: data.notes, vacations: data.vacations, idx, settings, today: ctx.today, start: from as LocalDate });
    const last = to < addDays(from as LocalDate, 6) ? to : addDays(from as LocalDate, 6);
    return week.filter((d) => d.day <= last);
  }, [data.habits, data.reminders, data.notes, data.vacations, idx, settings, ctx.today, from, to]);
  return (
    <section className="chat-card" aria-label={t('chat.cards.agenda')}>
      {days.map(({ day, agenda, entries }) => {
        const clashes = conflictNames(agenda.conflicts, entries);
        return (
          <div key={day} className="chat-card__day">
            <p className="chat-card__title">
              {day === ctx.today ? t('agenda.today') : fmtDate(day, { weekday: 'long', day: 'numeric', month: 'short' })}
              <span className="muted xsmall"> · {t('agenda.summary', { pending: agenda.pending, done: agenda.done })}</span>
            </p>
            {entries.length === 0 ? (
              <p className="muted small">{t('agenda.free')}</p>
            ) : (
              <ul className="chat-card__list">
                {entries.map((e) => (
                  <li key={e.key} className={`chat-card__row${e.done ? ' chat-card__row--done' : ''}`}>
                    {e.kind === 'date' && <CalendarDays size={14} aria-hidden="true" />}
                    <span className="chat-card__main">
                      <TimeRange entry={e} />
                      <span className="chat-card__name">{e.title}</span>
                      {e.overdue && <span className="badge badge--overdue">{t('habit.status.overdue')}</span>}
                      {clashes.get(e.key) && (
                        <span className="badge badge--warning">
                          <AlertTriangle size={12} aria-hidden="true" /> {t('agenda.overlaps', { names: clashes.get(e.key)!.join(', ') })}
                        </span>
                      )}
                    </span>
                    {e.done && <span className="badge badge--completed">{t('habit.status.completed')}</span>}
                  </li>
                ))}
              </ul>
            )}
          </div>
        );
      })}
      <Link to="/?view=agenda" className="chat-card__link">
        {t('chat.c.openAgenda')}
      </Link>
    </section>
  );
}

export function HabitsCard({ ids }: { ids: string[] }) {
  const { t } = useI18n();
  const { data, ctx, idx } = useApp();
  const settings = useMemo(() => toEngineSettings(ctx.settings), [ctx.settings]);
  const habits = ids.map((id) => data.habits.find((h) => h.id === id && !h.deletedAt && !h.archivedAt)).filter((h): h is HabitRec => Boolean(h));
  if (!habits.length) return <p className="chat__caption">{t('chat.cards.gone')}</p>;
  return (
    <section className="chat-card chat-card--habits" aria-label={t('chat.cards.habits')}>
      {habits.map((h) => {
        const item = todayFor(h, idx, data.vacations, settings, ctx.today);
        // Hoy toca: la tarjeta completa, con marcar y el resto de acciones. Si no, el enlace al detalle.
        if (item?.ue && item.ue.status !== 'not_scheduled' && item.ue.status !== 'out_of_range') return <HabitCard key={h.id} habit={h} item={item} streak={null} />;
        return (
          <Link key={h.id} to={`/habits/${h.id}`} className="chat-card__row chat-card__row--link">
            <span className="chat-card__name">{h.name}</span>
            <span className="muted xsmall">{t('chat.status.not_scheduled')}</span>
          </Link>
        );
      })}
    </section>
  );
}

export function TasksCard({ ids }: { ids: string[] }) {
  const { t, fmtDate } = useI18n();
  const { data, ctx } = useApp();
  const tasks = ids.map((id) => data.habits.find((h) => h.id === id && !h.deletedAt && !h.archivedAt)).filter((h): h is HabitRec => Boolean(h));
  if (!tasks.length) return <p className="chat__caption">{t('chat.cards.gone')}</p>;
  return (
    <section className="chat-card" aria-label={t('chat.cards.tasks')}>
      <ul className="chat-card__list">
        {tasks.map((h) => (
          <li key={h.id} className={`chat-card__row${h.completedAt ? ' chat-card__row--done' : ''}`}>
            <TaskCheck task={h} />
            <span className="chat-card__main">
              <span className="chat-card__name">{h.name}</span>
              <span className="muted xsmall">
                {h.dueDate
                  ? `${h.dueDate === ctx.today ? t('agenda.today') : fmtDate(h.dueDate, { weekday: 'short', day: 'numeric', month: 'short' })}${h.dueTime ? ` · ${h.dueTime}` : ''}`
                  : t('chat.a.undated')}
              </span>
              {h.dueDate && h.dueDate < ctx.today && !h.completedAt && <span className="badge badge--overdue">{t('habit.status.overdue')}</span>}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function TaskCheck({ task }: { task: HabitRec }) {
  const { t } = useI18n();
  const done = Boolean(task.completedAt);
  const [optimistic, setOptimistic] = useState<boolean | null>(null);
  if (optimistic !== null && optimistic === done) setOptimistic(null);
  const checked = optimistic ?? done;
  return (
    <input
      type="checkbox"
      checked={checked}
      aria-label={`${checked ? t('habit.actions.uncomplete') : t('habit.actions.complete')}: ${task.name}`}
      onChange={(e) => {
        setOptimistic(e.target.checked);
        void setTaskDone(task, e.target.checked).catch(() => setOptimistic(null));
      }}
    />
  );
}
